// Shared "Schedule Send" dialog. Opens a date+time (workspace timezone) picker
// and POSTs to /api/scheduled-emails. Reused by every direct-user email
// composer (mass email, invoice send-email, payment-email modal,
// booking-confirmation, lead/client one-off).
//
// Two callsites build the payload differently:
//
//   sourceType="one_off"      — payload is a fully-rendered email
//                                (to, cc, subject, htmlBody, attachments,
//                                 plus optional invoiceId/leadId/clientId
//                                 for activity logging at dispatch time).
//
//   sourceType="mass_email"   — payload is the bulk-send body
//                                (recipients{region, audience}, subject,
//                                 body, cc) — recipient expansion happens
//                                at dispatch time so audience changes
//                                between schedule and send are picked up.
//
// On success the dialog calls onScheduled() and the parent typically
// closes its composer. Cache for /api/scheduled-emails is invalidated
// so the Scheduled tab in Email Hub refreshes immediately.

import { useEffect, useState } from "react";
import { getWorkspaceTimeZone, getTimeZoneAbbreviation } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type ScheduledEmailSourceType = "one_off" | "mass_email";

interface ScheduledEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceType: ScheduledEmailSourceType;
  // Builder runs at confirm time so the payload can use the latest
  // composer state. Returning null cancels the schedule attempt.
  buildPayload: () => Record<string, any> | null;
  // Optional summary text shown at the top of the dialog ("Schedule
  // email to john@example.com" or "Schedule mass email to All Clients").
  recipientSummary?: string;
  // Subject line for the toast confirmation. If omitted we just say
  // "Email scheduled".
  subjectSummary?: string;
  onScheduled?: () => void;
  defaultDate?: string;
  defaultTime?: string;
  // Edit-time mode: when set, the dialog seeds the picker with this
  // ISO timestamp (rendered in CT) and calls onCustomSubmit on confirm
  // instead of POSTing a new schedule. Used by the Scheduled tab to
  // PATCH an existing pending row.
  initialScheduledFor?: string;
  confirmLabel?: string;
  onCustomSubmit?: (scheduledForIso: string) => Promise<void>;
}

// Convert an ISO UTC timestamp into the {date, time} pair the picker
// expects, rendered as CT. Mirrors the CT-skew dance below in reverse.
function isoToCtPickerParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: getWorkspaceTimeZone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const p: Record<string, string> = {};
  for (const part of fmt) if (part.type !== "literal") p[part.type] = part.value;
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    time: `${p.hour === "24" ? "00" : p.hour}:${p.minute}`,
  };
}

function todayISODate(): string {
  return new Date().toISOString().split("T")[0];
}

function defaultTime(): string {
  // Default to one hour from now, rounded down to the nearest 5 minutes.
  const t = new Date(Date.now() + 60 * 60 * 1000);
  const m = Math.floor(t.getMinutes() / 5) * 5;
  return `${String(t.getHours()).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function ScheduledEmailDialog({
  open,
  onOpenChange,
  sourceType,
  buildPayload,
  recipientSummary,
  subjectSummary,
  onScheduled,
  defaultDate,
  defaultTime: defaultTimeProp,
  initialScheduledFor,
  confirmLabel,
  onCustomSubmit,
}: ScheduledEmailDialogProps) {
  const { toast } = useToast();
  const seeded = initialScheduledFor ? isoToCtPickerParts(initialScheduledFor) : null;
  const [date, setDate] = useState<string>(seeded?.date || defaultDate || todayISODate());
  const [time, setTime] = useState<string>(seeded?.time || defaultTimeProp || defaultTime());

  // Re-seed when the dialog reopens with a new initial timestamp (edit
  // mode), so each row in the Scheduled tab pre-fills correctly.
  useEffect(() => {
    if (open && initialScheduledFor) {
      const p = isoToCtPickerParts(initialScheduledFor);
      setDate(p.date);
      setTime(p.time);
    }
  }, [open, initialScheduledFor]);

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      const sourcePayload = onCustomSubmit ? null : buildPayload();
      if (!onCustomSubmit && !sourcePayload) {
        throw new Error("Cannot schedule: required fields are missing");
      }
      // Treat the picker date+time as the workspace's configured
      // timezone. We build a wall-clock string and let JS resolve to UTC
      // by reading the offset for the chosen date (handles DST automatically).
      const localCT = new Date(`${date}T${time}:00`);
      // The Date above was parsed in the browser's local TZ, which is
      // wrong if the user isn't in the workspace zone. Compensate by
      // computing the offset between the local TZ and the workspace zone
      // for this instant.
      const ctFmt = new Intl.DateTimeFormat("en-US", {
        timeZone: getWorkspaceTimeZone(),
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).formatToParts(localCT);
      const ctParts: Record<string, string> = {};
      for (const p of ctFmt) if (p.type !== "literal") ctParts[p.type] = p.value;
      // What CT thinks "now" reads when given the browser-local
      // interpretation. Difference vs the picker's text gives us the
      // adjustment we need to apply.
      const ctReadback = new Date(
        `${ctParts.year}-${ctParts.month}-${ctParts.day}T${ctParts.hour}:${ctParts.minute}:${ctParts.second}`,
      );
      const skewMs = localCT.getTime() - ctReadback.getTime();
      const scheduledForUtc = new Date(localCT.getTime() + skewMs);

      if (onCustomSubmit) {
        await onCustomSubmit(scheduledForUtc.toISOString());
        return null;
      }

      const res = await apiRequest("POST", "/api/scheduled-emails", {
        scheduledFor: scheduledForUtc.toISOString(),
        sourceType,
        sourcePayload,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-emails"] });
      // Edit-time mode owns its own toast (so the "Updated" copy is
      // accurate). For the create path show the standard toast.
      if (!onCustomSubmit) {
        toast({
          title: "Email scheduled",
          description: `${subjectSummary ?? "Email"} will send ${date} at ${time} ${getTimeZoneAbbreviation(`${date}T${time}`)}.`,
        });
      }
      onScheduled?.();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({
        title: "Couldn't schedule",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const previewLabel = (() => {
    if (!date || !time) return null;
    try {
      return new Date(`${date}T${time}`).toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Schedule send</DialogTitle>
          <DialogDescription>
            {recipientSummary ?? "Pick when this email should go out. Times use your workspace timezone."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="scheduled-email-date">Date</Label>
            <Input
              id="scheduled-email-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={todayISODate()}
              data-testid="input-scheduled-email-date"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="scheduled-email-time">Time ({getTimeZoneAbbreviation(date && time ? `${date}T${time}` : new Date())})</Label>
            <Input
              id="scheduled-email-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="input-scheduled-email-time"
            />
          </div>
          {previewLabel && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <span className="text-muted-foreground">Will send:</span>
              <p className="mt-1 font-medium">{previewLabel} {getTimeZoneAbbreviation(`${date}T${time}`)}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={scheduleMutation.isPending}
            data-testid="button-scheduled-email-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => scheduleMutation.mutate()}
            disabled={!date || !time || scheduleMutation.isPending}
            data-testid="button-scheduled-email-confirm"
          >
            {scheduleMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {onCustomSubmit ? "Updating…" : "Scheduling…"}
              </>
            ) : (
              <>
                <Clock className="mr-2 h-4 w-4" />
                {confirmLabel ?? "Schedule send"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
